import type { SearchHit } from '@brain/contracts'
import { Button, Card, Chip, cn, EmptyState, Input, Select } from '@brain/ui'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  Bookmark,
  CalendarClock,
  CheckSquare,
  FileText,
  FolderGit2,
  HelpCircle,
  MessageSquare,
  Paperclip,
  Search,
  X,
} from 'lucide-react'
import { useDeferredValue, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CONTAINER_ICON } from '../../components/icons.ts'
import { SourceBadge } from '../../components/sources/SourceBadge.tsx'
import { containersQuery, useSearch } from '../../lib/hooks.ts'
import { loadSavedSearches, type SavedSearch, saveSearches } from '../../lib/saved-searches.ts'
import { sanitizeSnippet } from '../../lib/snippet.ts'
import { openTask } from '../../lib/ui-state.ts'

interface SearchParams {
  q?: string
  para?: 'project' | 'area' | 'resource'
  containerId?: string
  type?: string
  from?: string
  to?: string
  archived?: boolean
  /** cross-encoder reranking (slow on CPU) */
  precise?: boolean
}

const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined)

export const Route = createFileRoute('/_app/suche')({
  validateSearch: (s: Record<string, unknown>): SearchParams => ({
    q: str(s.q),
    para: (['project', 'area', 'resource'] as const).find((p) => p === s.para),
    containerId: str(s.containerId),
    type: str(s.type),
    from: str(s.from),
    to: str(s.to),
    archived: s.archived === true || s.archived === 'true' ? true : undefined,
    precise: s.precise === true || s.precise === 'true' ? true : undefined,
  }),
  component: SearchPage,
})

const NOTE_TYPES = [
  'note',
  'meeting',
  'decision',
  'daily',
  'reference',
  'brief',
  'person',
  'weekly_review',
] as const

function groupByContainer(hits: SearchHit[]) {
  const map = new Map<string, { title: string; hits: SearchHit[] }>()
  for (const h of hits) {
    const key = h.container?.id ?? (h.kind === 'container' ? h.id : 'inbox')
    const title = h.container?.title ?? (h.kind === 'container' ? h.title : '')
    const g = map.get(key) ?? { title, hits: [] }
    g.hits.push(h)
    map.set(key, g)
  }
  return [...map.entries()]
}

function SearchPage() {
  const { t } = useTranslation()
  const params = Route.useSearch()
  const navigate = useNavigate({ from: '/suche' })
  const [input, setInput] = useState(params.q ?? '')
  const deferred = useDeferredValue(input)
  const containers = useQuery(containersQuery({ includeArchived: true }))
  const [saved, setSaved] = useState<SavedSearch[]>(() => loadSavedSearches(globalThis.localStorage))
  const [help, setHelp] = useState(false)

  const setParams = (patch: Partial<SearchParams>) =>
    void navigate({ search: (prev: SearchParams) => ({ ...prev, ...patch }), replace: true })

  // input → URL. `written` holds the q values we put there ourselves (navigations can land after the next
  // keystroke), so the URL → input sync below only reacts to outside changes (sidebar link, back button)
  const written = useRef(new Set([params.q ?? '']))
  // biome-ignore lint/correctness/useExhaustiveDependencies: sync the deferred input into the URL only
  useEffect(() => {
    if (deferred !== (params.q ?? '')) {
      written.current.add(deferred)
      setParams({ q: deferred || undefined })
    }
  }, [deferred])
  // biome-ignore lint/correctness/useExhaustiveDependencies: reacts to URL changes only
  useEffect(() => {
    const q = params.q ?? ''
    if (written.current.has(q)) {
      // our latest value arrived – older ones can no longer come in
      if (q === deferred) written.current = new Set([q])
      return
    }
    written.current = new Set([q])
    setInput(q)
  }, [params.q])

  const result = useSearch({
    q: params.q ?? '',
    para: params.para,
    containerId: params.containerId,
    type: params.type as (typeof NOTE_TYPES)[number] | undefined,
    from: params.from,
    to: params.to,
    includeArchived: params.archived ?? false,
    limit: 50,
    ...(params.precise ? { rerank: 'true' as const } : {}),
  })

  const persist = (list: SavedSearch[]) => {
    setSaved(list)
    saveSearches(globalThis.localStorage, list)
  }

  const hits = result.data?.hits ?? []
  const groups = groupByContainer(hits)
  const filterCount = [
    params.para,
    params.containerId,
    params.type,
    params.from,
    params.to,
    params.archived,
  ].filter(Boolean).length

  return (
    <div className="min-w-0 flex-1 overflow-y-auto">
      <div className="mx-auto flex max-w-4xl flex-col gap-4 px-4 py-4 md:px-8 md:py-6">
        <header data-tauri-drag-region className="flex items-center gap-3">
          <h1 className="flex-1 text-[22px] text-text-1">{t('nav.search')}</h1>
          <Button size={32} variant="ghost" onClick={() => setHelp((h) => !h)} aria-expanded={help}>
            <HelpCircle size={16} />
            {t('search.operators')}
          </Button>
        </header>
        <div className="relative">
          <Search size={18} className="absolute top-1/2 left-4 -translate-y-1/2 text-meta" />
          <Input
            autoFocus
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t('search.placeholder')}
            className="h-12 pl-11 text-[15px]"
          />
        </div>
        {help && (
          <Card className="grid gap-1.5 p-4 text-[13px] text-text-2 sm:grid-cols-2">
            {(['exact', 'exclude', 'type', 'project', 'before', 'after'] as const).map((k) => (
              <p key={k}>
                <code className="rounded-[6px] bg-grey-2 px-1.5 py-0.5 font-mono text-[12px] text-text-1">
                  {t(`search.op.${k}.code`)}
                </code>{' '}
                {t(`search.op.${k}.text`)}
              </p>
            ))}
          </Card>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <Select
            fit
            className="h-8 text-[13px]"
            value={params.para ?? ''}
            onChange={(e) => setParams({ para: (e.target.value || undefined) as SearchParams['para'] })}
            aria-label={t('search.filter.para')}
          >
            <option value="">{t('search.filter.allPara')}</option>
            <option value="project">{t('para.project')}</option>
            <option value="area">{t('para.area')}</option>
            <option value="resource">{t('para.resource')}</option>
          </Select>
          <Select
            fit
            className="h-8 max-w-56 text-[13px]"
            value={params.containerId ?? ''}
            onChange={(e) => setParams({ containerId: e.target.value || undefined })}
            aria-label={t('search.filter.container')}
          >
            <option value="">{t('search.filter.allContainers')}</option>
            {(containers.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </Select>
          <Select
            fit
            className="h-8 text-[13px]"
            value={params.type ?? ''}
            onChange={(e) => setParams({ type: e.target.value || undefined })}
            aria-label={t('search.filter.type')}
          >
            <option value="">{t('search.filter.allTypes')}</option>
            {NOTE_TYPES.map((x) => (
              <option key={x} value={x}>
                {t(`noteType.${x}`)}
              </option>
            ))}
          </Select>
          <label className="flex items-center gap-1.5 text-[12px] text-meta">
            {t('search.filter.from')}
            <input
              type="date"
              value={params.from ?? ''}
              onChange={(e) => setParams({ from: e.target.value || undefined })}
              className="h-8 rounded-pill border border-line-2 bg-card px-3 text-[13px] text-text-1"
            />
          </label>
          <label className="flex items-center gap-1.5 text-[12px] text-meta">
            {t('search.filter.to')}
            <input
              type="date"
              value={params.to ?? ''}
              onChange={(e) => setParams({ to: e.target.value || undefined })}
              className="h-8 rounded-pill border border-line-2 bg-card px-3 text-[13px] text-text-1"
            />
          </label>
          <button
            type="button"
            aria-pressed={Boolean(params.archived)}
            onClick={() => setParams({ archived: params.archived ? undefined : true })}
            className={cn(
              'h-8 rounded-pill border px-3 font-medium text-[12px] transition-colors duration-150',
              params.archived ? 'border-pop bg-pop-tint text-pop-fg' : 'border-line-2 bg-card text-text-2',
            )}
          >
            {t('search.filter.archived')}
          </button>
          <button
            type="button"
            aria-pressed={Boolean(params.precise)}
            title={t('search.preciseHint')}
            onClick={() => setParams({ precise: params.precise ? undefined : true })}
            className={cn(
              'h-8 rounded-pill border px-3 font-medium text-[12px] transition-colors duration-150',
              params.precise ? 'border-pop bg-pop-tint text-pop-fg' : 'border-line-2 bg-card text-text-2',
            )}
          >
            {t('search.precise')}
          </button>
          {filterCount > 0 && (
            <Button
              size={32}
              variant="ghost"
              onClick={() =>
                setParams({
                  para: undefined,
                  containerId: undefined,
                  type: undefined,
                  from: undefined,
                  to: undefined,
                  archived: undefined,
                })
              }
            >
              <X size={14} />
              {t('search.filter.reset')}
            </Button>
          )}
          <span className="flex-1" />
          {params.q && (
            <Button
              size={32}
              variant="ghost"
              onClick={() =>
                persist(
                  [{ id: crypto.randomUUID(), label: params.q ?? '', params: { ...params } }, ...saved].slice(
                    0,
                    20,
                  ),
                )
              }
            >
              <Bookmark size={14} />
              {t('search.save')}
            </Button>
          )}
        </div>

        {saved.length > 0 && !params.q && (
          <Card className="p-3">
            <h2 className="px-2 pb-2 meta-label">{t('search.saved')}</h2>
            <div className="flex flex-wrap gap-1.5 px-1">
              {saved.map((s) => (
                <Chip key={s.id} className="pr-1">
                  <button
                    type="button"
                    onClick={() => {
                      setInput(s.params.q ?? '')
                      void navigate({ search: s.params as SearchParams, replace: true })
                    }}
                  >
                    {s.label}
                  </button>
                  <button
                    type="button"
                    aria-label={t('common.remove')}
                    onClick={() => persist(saved.filter((x) => x.id !== s.id))}
                  >
                    <X size={11} />
                  </button>
                </Chip>
              ))}
            </div>
          </Card>
        )}

        {!params.q ? (
          <EmptyState
            icon={<Search size={20} strokeWidth={1.9} />}
            title={t('search.startTitle')}
            description={t('search.startText')}
          />
        ) : result.isPending ? (
          <p className="text-[13px] text-meta">{t('common.loading')}</p>
        ) : result.isError ? (
          <p className="text-[13px] text-apricot-fg">{t('common.error')}</p>
        ) : hits.length === 0 ? (
          <EmptyState title={t('search.noResults')} description={t('search.noResultsText')} />
        ) : (
          <>
            <p className="flex flex-wrap items-center gap-2 text-[12px] text-meta">
              {t('search.count', { count: result.data?.total ?? hits.length })}
              {result.data?.mode && <span>· {t(`search.mode.${result.data.mode}`)}</span>}
              {result.data?.reranked && <span>· {t('search.mode.reranked')}</span>}
              <span className="flex-1" />
              <Link
                to="/chat"
                search={{ q: params.q }}
                className="flex items-center gap-1 font-medium text-pop-fg hover:underline"
              >
                <MessageSquare size={13} />
                {t('search.ask')}
              </Link>
            </p>
            {result.data?.weak && (
              <p className="rounded-tile bg-apricot-bg px-3 py-2 text-[13px] text-apricot-fg">
                {t('search.weak')}
              </p>
            )}
            {groups.map(([key, g]) => (
              <section key={key} className="flex flex-col gap-1.5">
                <h2 className="px-1 meta-label">{g.title || t('nav.inbox')}</h2>
                <Card className="divide-y divide-line-1">
                  {g.hits.map((h) => (
                    <HitRow key={`${h.kind}:${h.id}`} h={h} />
                  ))}
                </Card>
              </section>
            ))}
          </>
        )}
      </div>
    </div>
  )
}

function HitRow({ h }: { h: SearchHit }) {
  const { t } = useTranslation()
  const Icon =
    h.kind === 'container' && h.container
      ? CONTAINER_ICON[h.container.type]
      : h.kind === 'task'
        ? CheckSquare
        : h.kind === 'file'
          ? Paperclip
          : h.kind === 'source'
            ? FolderGit2
            : h.kind === 'meeting'
              ? CalendarClock
              : FileText
  const body = (
    <div className="flex gap-3 px-4 py-3 hover:bg-grey-1">
      <Icon
        size={16}
        strokeWidth={1.9}
        className={h.kind === 'source' ? 'mt-0.5 shrink-0 text-sky-fg' : 'mt-0.5 shrink-0 text-meta'}
      />
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 text-[14px] text-text-1">
          <span className="truncate">{h.title || t('note.untitled')}</span>
          {h.headingPath && <span className="truncate text-[12px] text-meta">› {h.headingPath}</span>}
          {h.noteType && <span className="text-[11px] text-meta">{t(`noteType.${h.noteType}`)}</span>}
          {h.container?.archived && <Chip className="h-5">{t('container.archived')}</Chip>}
        </p>
        {h.source && <SourceBadge source={h.source} />}
        {h.meeting && (
          <p className="text-[12px] text-meta">
            {t('search.transcriptHit', {
              date: new Date(h.meeting.startedAt).toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin' }),
            })}
          </p>
        )}
        <p
          className="mt-0.5 line-clamp-2 text-[13px] text-text-2 [&_mark]:rounded-[3px] [&_mark]:bg-pop-tint [&_mark]:px-0.5 [&_mark]:text-pop-fg"
          // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitizeSnippet only lets <mark> through
          dangerouslySetInnerHTML={{ __html: sanitizeSnippet(h.snippet) }}
        />
      </div>
    </div>
  )
  if (h.kind === 'note')
    return (
      <Link to="/n/$noteId" params={{ noteId: h.id }}>
        {body}
      </Link>
    )
  if (h.kind === 'container')
    return (
      <Link to="/c/$containerId" params={{ containerId: h.id }}>
        {body}
      </Link>
    )
  if (h.kind === 'source' && h.container)
    return (
      <Link
        to="/c/$containerId"
        params={{ containerId: h.container.id }}
        search={{ tab: 'sources', doc: h.id }}
      >
        {body}
      </Link>
    )
  if (h.kind === 'meeting')
    return (
      <Link
        to="/meetings/$meetingId"
        params={{ meetingId: h.id }}
        search={{ t: h.meeting?.startMs ?? 0, tab: 'transcript' }}
      >
        {body}
      </Link>
    )
  if (h.kind === 'file' && h.container)
    return (
      <Link
        to="/c/$containerId"
        params={{ containerId: h.container.id }}
        search={{ tab: 'files', file: h.id }}
      >
        {body}
      </Link>
    )
  return (
    <button type="button" onClick={() => openTask(h.id)} className="block w-full text-left">
      {body}
    </button>
  )
}
